import {} from '@popperjs/core'; // fix for pnpm not portable typescript
import type { WeakValidationMap } from 'react';
import { globalAttributesPropTypes, PropTypes, xstyledPropTypes } from '@elliemae/ds-legacy-props-helpers';
import type {} from '@elliemae/ds-legacy-utilities';
import type { GlobalAttributesT, XstyledProps } from '@elliemae/ds-legacy-props-helpers';
import type { usePopper } from 'react-popper';

export declare namespace DSPopperJST {
  export type PopperPlacementsT =
    | 'top-start'
    | 'top'
    | 'top-end'
    | 'right-start'
    | 'right'
    | 'right-end'
    | 'bottom-end'
    | 'bottom'
    | 'bottom-start'
    | 'left-end'
    | 'left'
    | 'left-start';
  export interface DefaultProps {
    closeContextMenu: () => void;
    withoutPortal: boolean;
    withoutArrow: boolean;
    withoutAnimation: boolean;
    animationDuration: number;
    startPlacementPreference: PopperPlacementsT;
    placementOrderPreference: PopperPlacementsT[];
    zIndex: number;
    extraPopperStyles: Record<string, unknown>;
    onClickOutside: (e: MouseEvent | TouchEvent) => void;
    customOffset: [number, number];
  }
  export interface OptionalProps {
    boundaryElement?: HTMLElement;
    portalDOMContainer?: HTMLElement;
    modifiers?: NonNullable<Parameters<typeof usePopper>[2]>['modifiers'];
    actionRef?: React.MutableRefObject<{
      update: ReturnType<typeof usePopper>['update'];
    }>;
  }
  export interface RequiredProps {
    children: ReactChildrenComplete;
    referenceElement: HTMLElement;
    showPopover: boolean;
  }

  export interface Props
    extends Partial<DefaultProps>,
      OptionalProps,
      Omit<GlobalAttributesT<HTMLButtonElement>, keyof DefaultProps | keyof OptionalProps | keyof RequiredProps>,
      Omit<XstyledProps, 'zIndex'>,
      RequiredProps {}

  export interface InternalProps
    extends DefaultProps,
      OptionalProps,
      Omit<GlobalAttributesT<HTMLButtonElement>, keyof DefaultProps | keyof OptionalProps | keyof RequiredProps>,
      Omit<XstyledProps, 'zIndex'>,
      RequiredProps {}
}

const placementPreferenceArray = [
  'top-start',
  'top',
  'top-end',
  'right-start',
  'right',
  'right-end',
  'bottom-end',
  'bottom',
  'bottom-start',
  'left-end',
  'left',
  'left-start',
];
export const dspopperjsPropTypes = {
  ...globalAttributesPropTypes,
  ...xstyledPropTypes,
  /**
   * The content of the popper
   */
  children: PropTypes.node.description('The content of the popper').isRequired,
  /**
   * The DOM element that needs to be used as a reference for positioning the popper.
   */
  referenceElement: PropTypes.oneOfType([PropTypes.node, PropTypes.oneOf([null])]).description(
    'The DOM element that needs to be used as a reference for positioning the popper.',
  ).isRequired,
  /**
   * Wheter or not to show the popper content
   */
  showPopover: PropTypes.bool.description('whether or not to show the popper content').isRequired,
  /**
   * Callback to close the context menu, used for "on click outside",
   *  if not provided click outside will not trigger anything
   */
  closeContextMenu: PropTypes.func
    .description(
      'Callback to close the context menu, used for "on click outside",' +
        ' if not provided click outside will not trigger anything',
    )
    .defaultValue('() => {}'),
  /**
   *  Wheter or not the popper content should appear in a DOM portal or not
   */
  withoutPortal: PropTypes.bool
    .description('Whether or not the popper content should appear in a DOM portal or not')
    .defaultValue(true),
  /**
   *  Wheter or not the popper context menu should print the arrow
   */
  withoutArrow: PropTypes.bool
    .description('Whether or not the popper context menu should print the arrow')
    .defaultValue(false),
  /**
   *  Whether or not the popper context menu should be animated
   */
  withoutAnimation: PropTypes.bool
    .description('Whether or not the popper context menu should be animated')
    .defaultValue(false),
  /**
   *  Popper context menus Animation duration in ms
   */
  animationDuration: PropTypes.number.description('Popper context menus Animation duration in ms').defaultValue(100),
  /**
   *  start placement preferences, as per popperjs placement option
   */
  startPlacementPreference: PropTypes.oneOf(placementPreferenceArray)
    .description('start placement preferences, as per popperjs placement option')
    .defaultValue("'top'"),
  /**
   *  Array of placement preferences, as per popperjs "flip" placement option
   */
  placementOrderPreference: PropTypes.arrayOf(PropTypes.oneOf(placementPreferenceArray))
    .description('Array of placement preferences, as per popperjs "flip" placement option')
    .defaultValue("['top', 'bottom', 'left', 'right']"),
  /**
   *  popperjs content z-index
   */
  zIndex: PropTypes.number.description('popperjs content z-index').defaultValue('theme.zIndex.popper'),
  /**
   *  placement offset array
   */
  customOffset: PropTypes.arrayOf(PropTypes.number).description('placement offset array').defaultValue('[0,14]'),
  extraPopperStyles: PropTypes.object
    .description('Extra object styles to attach to the popper element')
    .defaultValue({}),
  onClickOutside: PropTypes.func
    .description('Callback triggered when clicking outside the popper')
    .defaultValue(() => null),
  /**
   *  Bounding element to calculate upon, defaults to it is "clippingParents",
   *   which are the scrolling containers that may cause the element to be partially or fully cut off.
   */
  boundaryElement: PropTypes.oneOfType([PropTypes.node, PropTypes.oneOf([null])]).description(
    'Bounding element to calculate upon, defaults to "clippingParents",' +
      'which are the scrolling containers that may cause element to be partially or fully cut off',
  ),
  /**
   *  When using portal, the container in which to append the DOM content, defaults to document body
   */
  portalDOMContainer: PropTypes.oneOfType([PropTypes.node, PropTypes.oneOf([null])]).description(
    'When using portal, the container in which to append the DOM content, defaults to document body',
  ),
  /**
   *  modifiers array for full-custom popper-js override
   *  https://popper.js.org/docs/v2/modifiers/
   */
  modifiers: PropTypes.array.description(
    'If you provide the modifiers array for full-custom override in popper.js,' +
      ' it means you are responsible for completely overwriting all the existing modifiers.' +
      ' This includes the modifiers that control "boundaryElement," "withoutArrow," "placementOrderPreference," and "customOffset".' +
      'For more detailed information, please refer to the documentation at https://popper.js.org/docs/v2/modifiers/.',
  ),
  actionRef: PropTypes.object.description('Ref object to access the popperjs instance methods'),
} as WeakValidationMap<unknown>;
